import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, exhaustMap, Observable, of } from 'rxjs';

import { AuthService } from '@ninety/platform/services/services/auth.service';
import { DatadogLogger } from '@ninety/platform/services/vendor/datadog';
import { EntitlementsService } from '@ninety/platform/state';
import { environment } from '@ninety/ui/web/environments';

const apiBasePaths = [
  environment.hostedEnvironment,
  environment.apiOrigin,
  environment.mazApiOrigin,
  environment.meetingAgentApiOrigin,
];

export function EntitlementsInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const entitlementsService = inject(EntitlementsService);
  const authService = inject(AuthService);
  const logger = inject(DatadogLogger);

  // avoid infinite loop, logout APIs exempt
  if (
    req.url.includes(environment.entitlementsApiOrigin) ||
    req.headers.has('entitlements') ||
    req.url.toLowerCase().includes('/refresh') ||
    req.url.toLowerCase().includes('/logout') ||
    (req.url.toLowerCase().includes('/login') && !req.url.toLowerCase().includes('/microsoft/login')) ||
    req.url.toLowerCase().endsWith('/init')
  ) {
    return next(req);
  }

  const shouldAddHeader = apiBasePaths.some(basePath => req.url.startsWith(basePath));
  if (!shouldAddHeader) {
    return next(req);
  }

  // NOTE: catchError must wrap only the token-acquisition observables, never `next.handle`.
  // If it covers `next.handle`, any 4XX/5XX response is caught and the request is re-issued
  // without the entitlements header, which for non-free tier requests/services, the backend
  // will always reject with a 403. See ticket/PR for DEV-25936 for more details.
  return authService.getRefreshedAccessToken().pipe(
    catchError(() => {
      logger.error({ message: 'entitlements_interceptor_error', data: { method: req.method, url: req.url } });
      return of(null);
    }),
    exhaustMap(authToken => {
      if (!authToken) {
        logger.warn({
          message: 'entitlements_interceptor_no_auth_token',
          data: { method: req.method, url: req.url },
        });
        return next(req);
      }
      return entitlementsService.getOrFetchEntitlementsToken(authToken).pipe(
        catchError(() => {
          logger.error({
            message: 'entitlements_interceptor_error',
            data: { method: req.method, url: req.url },
          });
          return of('');
        }),
        exhaustMap(entitlementsToken => {
          if (!entitlementsToken) {
            logger.warn({
              message: 'entitlements_interceptor_no_token',
              data: { method: req.method, url: req.url },
            });
            return next(req);
          }
          const clonedRequest = req.clone({
            setHeaders: {
              entitlements: entitlementsToken,
            },
          });
          return next(clonedRequest);
        })
      );
    })
  );
}
